import { FlexCell, FlexRow } from "@/common/StyledComponents";
import { formattedCurrency, sum } from "@/utils";
import { Divider, Grid, Paper, Tooltip, useTheme } from "@mui/material";
import {
    selectLines,
    selectProjectCurrency,
    selectTotals,
} from "./actualSlice";
import { useSelector } from "react-redux";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";

const TotalRow = ({
    rowLabelComponent,
    estimated,
    running,
    runningVariance,
    actual,
    actualVariance,
    color,
    fontWeight,
    estimatedTooltip,
    runningTooltip,
    runningVarianceTooltip,
    actualTooltip,
    actualVarianceTooltip,
}) => {
    return (
        <>
            <FlexRow style={{ height: 40 }}>
                <FlexCell minWidth={115}>{rowLabelComponent}</FlexCell>
                <TotalFlexCell
                    total={estimated}
                    tooltip={estimatedTooltip}
                    fontWeight={fontWeight}
                    color={color}
                />
                <TotalFlexCell
                    total={actual}
                    tooltip={actualTooltip}
                    fontWeight={fontWeight}
                    color={color}
                />
                <div style={{ paddingRight: 40 }}>
                    <TotalFlexCell
                        total={actualVariance}
                        tooltip={actualVarianceTooltip}
                        fontWeight={fontWeight}
                        color={color}
                    />
                </div>
                <Divider orientation="vertical" />
                <div style={{ paddingLeft: 40 }}>
                    <TotalFlexCell
                        total={running}
                        tooltip={runningTooltip}
                        fontWeight={fontWeight}
                        color={color}
                    />
                </div>
                <TotalFlexCell
                    total={runningVariance}
                    tooltip={runningVarianceTooltip}
                    fontWeight={fontWeight}
                    color={color}
                />
            </FlexRow>
            <Divider sx={{ borderColor: "#aaaaaa44" }} />
        </>
    );
};

const TotalFlexCell = ({ total, color, fontWeight, tooltip }) => {
    const theme = useTheme();
    const isNumber = !isNaN(total);
    const projectCurrency = useSelector(selectProjectCurrency);
    color = total == "N/A" ? "#aaaaaaaa" : color;
    return (
        <>
            <FlexCell align="right" style={{ maxWidth: 140, minWidth: 150 }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "right",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    {tooltip && (
                        <Tooltip title={tooltip}>
                            <HelpRoundedIcon
                                fontSize="10px"
                                sx={{ color: "gray" }}
                            />
                        </Tooltip>
                    )}
                    {
                        <span
                            style={{
                                fontWeight: fontWeight ? fontWeight : 400,
                                color: color
                                    ? color
                                    : total < 0
                                      ? theme.palette.error.main
                                      : theme.palette.text.primary,
                            }}
                        >
                            {isNumber
                                ? formattedCurrency(
                                      total,
                                      projectCurrency,
                                      false,
                                      2,
                                  )
                                : total}
                        </span>
                    }
                </div>
            </FlexCell>
        </>
    );
};

const ProfitNumber = ({ amount, margin }) => {
    const theme = useTheme();
    const projectCurrency = useSelector(selectProjectCurrency);
    const percentageColor =
        !isNaN(amount) && amount < 0 ? theme.palette.error.main : "gray";
    const amountColor =
        !isNaN(amount) && amount < 0 && theme.palette.error.main;
    return (
        <Grid container justifyContent={"right"} alignItems={"center"}>
            <Grid
                item
                sx={{ color: percentageColor, paddingRight: 0.6, fontSize: 10 }}
            >{`(${(margin * 100).toFixed(1)}%)`}</Grid>
            <Grid item sx={{ color: amountColor }}>
                {formattedCurrency(amount, projectCurrency, false, 2)}
            </Grid>
        </Grid>
    );
};

export default function ActualTotals() {
    const totals = useSelector(selectTotals);
    const actualLines = useSelector(selectLines);

    const actualLinesArray = Object.values(actualLines);

    const externalBudget = totals.external_budget;
    const externalActual = sum(
        actualLinesArray
            .filter((line) => !line.is_internal)
            .map((line) => line.actual_project),
    );
    const externalActualVariance = totals.external_budget - externalActual;
    const externalRunning = totals.external_budget;
    const externalRunningVariance = totals.external_budget - externalRunning;

    const internalBudget = totals.internal_budget;
    const internalActual = totals.float_internal_scheduled;
    const internalActualVariance =
        totals.internal_budget - totals.float_internal_scheduled;
    const internalRunning = totals.float_internal_scheduled;
    const internalRunningVariance =
        totals.internal_budget - totals.float_internal_scheduled;

    const budget = totals.total_budget;
    const actual = externalActual + internalActual;
    const running = externalRunning + internalRunning;
    const actualVariance = totals.total_budget - actual;
    const runningVariance = totals.total_budget - running;

    const quoted = totals.total_quoted;
    const billed = totals.billed;
    const quotedActualVarience = quoted - billed;

    const grossProfit = totals.gross_profit;
    const grossProfitMargin = totals.total_quoted
        ? totals.gross_profit / totals.total_quoted
        : 0;
    const actualGrossProfit = totals.billed - externalActual;
    const actualGrossProfitMargin = totals.billed
        ? actualGrossProfit / totals.billed
        : 0;

    return (
        <>
            <Paper
                sx={{
                    padding: "10px 35px 25px 35px",
                    boxShadow: "none",
                    overflow: "auto",
                }}
            >
                <TotalRow
                    color={"gray"}
                    rowLabelComponent={<h4>Budget</h4>}
                    estimated={"Estimated"}
                    running={"Running"}
                    runningVariance={"Running Remaining"}
                    actual={"Actual"}
                    actualVariance={"Actual Remaining"}
                />
                <TotalRow
                    fontWeight={600}
                    rowLabelComponent={
                        <span style={{ fontWeight: 600 }}>- Total Costs</span>
                    }
                    estimated={budget}
                    estimatedTooltip={
                        "Budget Total: Total of all bids' budgets, aka the project budget. Does not include markup."
                    }
                    running={running}
                    runningTooltip={
                        "Running Total: Total of all actual lines' running amount + Float Scheduled"
                    }
                    runningVariance={runningVariance}
                    runningVarianceTooltip={"Budget Total - Running Total"}
                    actual={actual}
                    actualTooltip={
                        "Actual Total: Total of actual lines’ allocation + Float Scheduled"
                    }
                    actualVariance={actualVariance}
                    actualVarianceTooltip={"Budget Total - Actual Total"}
                />
                <TotalRow
                    rowLabelComponent={
                        <span style={{ marginLeft: 30 }}>- External Costs</span>
                    }
                    estimated={externalBudget}
                    estimatedTooltip={
                        "Hard Cost Budget Total: Total of all bids' budget, but only include hard cost lines"
                    }
                    running={externalRunning}
                    runningTooltip={
                        "Running Total: but only those associated with a hard cost line item"
                    }
                    runningVariance={externalRunningVariance}
                    runningVarianceTooltip={
                        "Hard Cost Budget Total - Hard Cost Running Total"
                    }
                    actual={externalActual}
                    actualTooltip={
                        "Hard Cost Actual Total: Actual Total, but only those associated with a hard cost line item"
                    }
                    actualVariance={externalActualVariance}
                    actualVarianceTooltip={
                        "Hard Cost Budget Total - Hard Cost Actual Total"
                    }
                />
                <TotalRow
                    rowLabelComponent={
                        <span style={{ marginLeft: 30 }}>- Internal Costs</span>
                    }
                    estimated={internalBudget}
                    estimatedTooltip={
                        "Internal Budget Total: Total of all bids' budget, but only include hours lines"
                    }
                    running={internalRunning}
                    runningTooltip={
                        "Float Scheduled: all hours added to the Float's Schedule page"
                    }
                    runningVariance={internalRunningVariance}
                    runningVarianceTooltip={
                        "Internal Budget Total - Float Scheduled"
                    }
                    actual={internalActual}
                    actualTooltip={
                        "Float Scheduled: all hours added to the Float's Schedule page"
                    }
                    actualVariance={internalActualVariance}
                    actualVarianceTooltip={
                        "Internal Budget Total - Float Scheduled"
                    }
                />
            </Paper>
            <br />
            <Paper
                sx={{
                    padding: "10px 35px 25px 35px",
                    boxShadow: "none",
                    overflow: "auto",
                }}
            >
                <TotalRow
                    color={"gray"}
                    rowLabelComponent={<h4>Profitability</h4>}
                    estimated={"Estimated"}
                    running={
                        <span style={{ color: "#aaaaaaaa" }}>Running</span>
                    }
                    runningVariance={
                        <span style={{ color: "#aaaaaaaa" }}>
                            Running Remaining
                        </span>
                    }
                    actual={"Actual"}
                    actualVariance={"Actual Remaining"}
                />
                <TotalRow
                    fontWeight={600}
                    rowLabelComponent={
                        <span style={{ fontWeight: 600 }}>- Total Quoted</span>
                    }
                    estimated={quoted}
                    estimatedTooltip={
                        "Quoted Total: Total of all bids' quoted amount, aka project quoted"
                    }
                    running={"N/A"}
                    runningVariance={"N/A"}
                    actual={billed}
                    actualTooltip={
                        "Bills Total: Total of bills that are marked Sent"
                    }
                    actualVariance={quotedActualVarience}
                    actualVarianceTooltip={"Quoted Total - Bills Total"}
                />
                <TotalRow
                    rowLabelComponent={
                        <span style={{ marginLeft: 30 }}>- Gross Profit</span>
                    }
                    estimated={
                        <ProfitNumber
                            amount={grossProfit}
                            margin={grossProfitMargin}
                        />
                    }
                    estimatedTooltip={
                        "Quoted Gross Profit: Quoted Total - Hard Cost Budget Total"
                    }
                    running={"N/A"}
                    runningVariance={"N/A"}
                    actual={
                        <ProfitNumber
                            amount={actualGrossProfit}
                            margin={actualGrossProfitMargin}
                        />
                    }
                    actualTooltip={
                        "Actual Gross Profit: Bills Total - Actual Hard Cost"
                    }
                    actualVariance={"N/A"}
                />
            </Paper>
        </>
    );
}
